import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import styled, { css, useTheme } from "styled-components";
import Link from "common/Typography/Link";
import { CreateAccountIcon, DemoAccountIcon } from "common/Icons/Icons";
import Flex from "common/Boxes/Flex";
import { typography } from "@shared/common/Themes/typography";
import { CSSProperties, useContext } from "react";
import useSharedWorkspace from "@shared/features/workspaces/useSharedWorkspace";
import { LanguageNameSpaces, WorkspaceType } from "@shared/types";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { useBottomNavigator } from "utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useCreateAccountClick } from "features/portfolio/common/hooks.tsx";
import { IS_WHOP } from "common/constants.ts";
import { useSelectUser } from "@shared/utils/hooks/misc.ts";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import {
  getPracticeExchange,
  useTradingIntent,
} from "features/onboarding/guided/intent";
import { markOnboardingLiveChoice } from "features/onboarding/common/utils";
import { ONBOARDING_SWITCH_MODE_ID } from "testIds";
import useQuery from "utils/hooks/useQuery.ts";
import { ArrowRight, FlaskConical, Plug } from "lucide-react";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
  getPanelShadow,
} from "common/NewLook/tokens.ts";

interface ChangeAccountTypeMessageProps {
  style?: CSSProperties;
  /** New look: a single quiet row under a hairline instead of a panel. */
  inline?: boolean;
}

const ChangeAccountTypeMessage = ({
  style,
  inline,
}: ChangeAccountTypeMessageProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();
  const { resetFormValues } = useContext(AccountFormContext)!;
  const { setWorkspaceType, workspace } = useSharedWorkspace({
    allowAdminWorkspace: true,
  });
  const query = useQuery();
  const primaryExchange = query.get("exchange") ?? undefined;
  const { onCreateAccountClick, whopModal } =
    useCreateAccountClick(primaryExchange);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isDemoWorkspace = workspace === WorkspaceType.DEMO;
  const user = useSelectUser();

  const onboarding = useContext(OnboardingContext);
  const { intent } = useTradingIntent();
  const onClickLink = () => {
    if (onboarding && intent) {
      if (isDemoWorkspace) markOnboardingLiveChoice();
      resetFormValues();
      setWorkspaceType(
        isDemoWorkspace ? WorkspaceType.USER : WorkspaceType.DEMO
      );
      onboarding.changeStep(
        isDemoWorkspace
          ? OnboardingDetailedStep.SELECT_EXCHANGE
          : intent === "dca"
          ? OnboardingDetailedStep.COPY_DCA_BOT
          : OnboardingDetailedStep.TRADING_METHOD,
        {
          method: intent,
          guided: isDemoWorkspace ? "0" : "1",
          prepareDemo: "",
          connectLive: isDemoWorkspace ? "1" : "",
          exchange: isDemoWorkspace ? "" : getPracticeExchange(intent),
          setup: "",
          "account-id": "",
          preset: "",
        }
      );
      return;
    }
    if (IS_WHOP && (!user?.is_onboarding || isDemoWorkspace)) {
      onCreateAccountClick(true);
    } else {
      resetFormValues();
      const updateWorkspace = isDemoWorkspace
        ? WorkspaceType.USER
        : WorkspaceType.DEMO;
      setWorkspaceType(updateWorkspace);
    }
  };

  const title = isDemoWorkspace
    ? t("create_account.live_account_question")
    : t("create_account.not_sure_question");

  const linkText = isDemoWorkspace
    ? t("create_account.connect_exchange")
    : t("create_account.continue_demo");

  const link = (
    <Link
      data-testid={ONBOARDING_SWITCH_MODE_ID}
      onClick={onClickLink}
      style={{ marginLeft: isDemoWorkspace || isBottomNavigator ? 0 : 4 }}
    >
      {linkText}
    </Link>
  );

  const description = isDemoWorkspace
    ? ""
    : t("create_account.demo_description");

  if (theme.isNewLook && inline) {
    return (
      <InlineRow style={style}>
        <span>
          {isDemoWorkspace
            ? t("create_account.new_look.live_prompt")
            : t("create_account.new_look.demo_prompt")}
        </span>
        <InlineLink
          type="button"
          data-testid={ONBOARDING_SWITCH_MODE_ID}
          onClick={onClickLink}
        >
          {isDemoWorkspace
            ? t("create_account.new_look.live_link")
            : t("create_account.new_look.demo_link")}
          <ArrowRight
            size={14}
            strokeWidth={NEW_LOOK_ICON_STROKE}
            aria-hidden
          />
        </InlineLink>
        {whopModal}
      </InlineRow>
    );
  }

  // New look: a quiet inset note (no tinted panel, no icon tile).
  if (theme.isNewLook) {
    const NoteIcon = isDemoWorkspace ? Plug : FlaskConical;
    return (
      <NewLookPanel style={style}>
        <NewLookIcon>
          <NoteIcon size={18} strokeWidth={NEW_LOOK_ICON_STROKE} aria-hidden />
        </NewLookIcon>
        <NewLookContent>
          <NewLookTitle>{title}</NewLookTitle>
          <NewLookText>
            {!isBottomNavigator && description}
            {link}
          </NewLookText>
        </NewLookContent>
        {whopModal}
      </NewLookPanel>
    );
  }

  const icon = isDemoWorkspace ? (
    <StyledCreateAccountIcon />
  ) : (
    <StyledDemoAccountIcon colors={[...theme.colors.demoTagColors].reverse()} />
  );

  return (
    <MessageBg style={style}>
      {icon}
      <ContentWrapper>
        <Title level={4} $weight={typography.fontWeights.BOLD}>
          {title}
        </Title>
        <Text>
          {!isBottomNavigator && description}
          {link}
        </Text>
      </ContentWrapper>
      {whopModal}
    </MessageBg>
  );
};

export default ChangeAccountTypeMessage;

const MessageBg = styled(Flex)`
  background-color: ${(p) => p.theme.colors.lightBlue};
  justify-content: flex-start;
  padding: 16px;
  border-radius: 8px;
  text-align: start;
  width: 100%;
`;
const iconStyle = css`
  width: 37px;
  height: 32px;
`;
const StyledCreateAccountIcon = styled(CreateAccountIcon)`
  fill: ${(p) => p.theme.colors.primaryBlue};
  ${iconStyle};
`;
const StyledDemoAccountIcon = styled(DemoAccountIcon)`
  ${iconStyle};
`;
const ContentWrapper = styled.div`
  margin-left: 16px;
  flex: 1;
`;
const NewLookPanel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: ${(p) => getPanelShadow(p.theme)};
  text-align: start;
`;
/* Centred on the title's first line (22px), however many lines it wraps to. */
const NewLookIcon = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  height: 22px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const NewLookTitle = styled.div`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NewLookText = styled.div`
  ${newLookType.body};
  font-size: 13px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  a {
    font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  }
`;
const InlineRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 12px;
  width: 100%;
  padding-top: 16px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.body};
  font-size: 13px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const InlineLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: none;
  font: inherit;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).accent};
  cursor: pointer;
  svg {
    transition: transform 150ms ease;
  }
  &:hover svg {
    transform: translateX(2px);
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).accent};
    outline-offset: 2px;
  }
`;
